import React, {
  ChangeEvent,
  useMemo,
  useState,
  useContext,
  useEffect,
} from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputLabel,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Business,
  CheckCircle,
  Dns,
  ErrorOutline,
  LocalShipping,
  Public,
  WarningAmber,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';

import {
  IBroker,
  RecipientType,
  TransmissionFormat,
} from 'api/bitedb/brokers/brokers';

import { IProduct, PaginationParams } from 'api/bitedb/products/products';

import { useGetBrokersByGroupId } from 'api/bitedb/brokers/brokers.hooks';
import { ContactAPI } from 'api/ContactAPI';
import { EmailAPI } from 'api/EmailAPI';
import { BiteDBAPI } from 'api/BiteDBAPI';
import { ProductServiceAPI } from 'api/ProductServiceAPI';
import { IColumnHeader, IExternalTemplate } from 'interfaces/IFileInterfaces';

import { useGetProductsByDatabaseId } from 'api/bitedb/products/products.hooks';
import { IDatabase } from 'api/bitedb/database/database';
import AddExportTemplateModal from './AddExportTemplateModal';
import MasterTable from '../Tables/MasterTable';
import ProductPageRow, {
  withResolvedBrokerProductValue,
} from '../Tables/ProductRowSelectItems';
import ProductPageColumns from '../Tables/ProductColumnsSelectItems';
import { AddUploadModal } from './AddUploadModal';
import UploadColumns, { ColumnKeys } from '../Tables/UploadColumnsSelect';
import UploadRow from '../Tables/UploadRowSelect';
import FileAPI from 'api/FileAPI';
import { IUpload } from 'api/bitedb/files/file';
import { isHiddenAdditionalColumn } from 'pages/import/lib/additional-columns';

// MPF/HMF are excluded from broker packages, and "Total Duties" (totalCost)
// only repeated Duty Cost; templates saved before that may still list them.
const isExcludedBrokerColumn = (value: string): boolean =>
  value === 'mpf' ||
  value === 'hmf' ||
  value === 'totalCost' ||
  isHiddenAdditionalColumn(value);

type BrokerBomRow = IProduct & { bom_depth: number };

const childProductCount = (product: unknown): number =>
  Number((product as { child_products?: unknown })?.child_products ?? 0) || 0;

// Components carry the BOM link's quantity, UOM, and unit value, not the
// component's own product-level values.
const toBrokerBomRow = (child: any, depth: number): BrokerBomRow => ({
  ...withResolvedBrokerProductValue({
    ...child,
    quantity: child.relationship_quantity ?? child.quantity,
    product_value: child.relationship_unit_value ?? child.product_value,
    additional_columns: child.relationship_unit_of_measure
      ? {
          ...(child.additional_columns ?? {}),
          unit_of_measure: child.relationship_unit_of_measure,
        }
      : child.additional_columns,
  }),
  bom_depth: depth,
});

const loadBomBreakdown = async (
  lines: IProduct[],
  databaseId: string,
): Promise<Record<string, BrokerBomRow[]>> => {
  const rootIds = lines
    .filter((line) => line.id && childProductCount(line) > 0)
    .map((line) => String(line.id));
  if (!rootIds.length) return {};
  const { success, result } = await ProductServiceAPI.getBomTree(
    databaseId,
    rootIds,
  );
  if (!success) {
    throw new Error(
      typeof result === 'string' ? result : 'Failed to load BOM components',
    );
  }
  const trees = new Map<string, BrokerBomRow[]>();
  for (const component of Array.isArray(result) ? result : []) {
    const rootId = String(component?.root_id ?? '');
    if (!rootId || !component?.id) continue;
    const rows = trees.get(rootId) ?? [];
    rows.push(toBrokerBomRow(component, Number(component.depth) || 1));
    trees.set(rootId, rows);
  }
  // A selected line already shown inside another selected line's BOM keeps
  // its own row but not a second copy of its components.
  const selectedIds = new Set(lines.map((line) => String(line.id)));
  const nestedIds = new Set<string>();
  trees.forEach((rows) =>
    rows.forEach((row) => {
      if (selectedIds.has(String(row.id))) nestedIds.add(String(row.id));
    }),
  );
  return Object.fromEntries(
    Array.from(trees.entries()).filter(([id]) => !nestedIds.has(id)),
  );
};

interface TransmissionIssue {
  model_number?: string;
  message: string;
}

interface TransmissionValidation {
  isValid: boolean;
  errors: TransmissionIssue[];
  warnings: TransmissionIssue[];
}

interface SendToBrokerWizardState {
  recipientType: RecipientType | null;
  recipientId: string | null;
  format: TransmissionFormat;
  formatId: string;
  selectedLines: IProduct[];
  selectedDocuments: IUpload[];
  columnHeaders: IColumnHeader[];
  includeBomBreakdown: boolean;
  includeEvidence: boolean;
  acknowledgeWarnings: boolean;
  notes: string;
}

interface ISendToBrokerWizardProps {
  database?: Partial<IDatabase> & { database_structure?: string | null };
  open: boolean;
  handleClose: () => void;
  // Broker records carry the address in `email`; the summary's
  // `contactEmail` is always blank. Import Hub opts in to the fix.
  readRecipientEmail?: boolean;
}

const defaultColumnHeaders: IColumnHeader[] = [
  { label: 'Product', value: 'name', isDisplayed: true },
  { label: 'Description', value: 'description', isDisplayed: true },
  { label: 'SKU', value: 'model_number', isDisplayed: true },
  { label: 'HTS Code', value: 'hts_code', isDisplayed: true },
  { label: 'Quantity', value: 'quantity', isDisplayed: true },
  { label: 'Value', value: 'product_value', isDisplayed: true },
  { label: 'Country of Origin', value: 'country_of_origin', isDisplayed: true },
  { label: 'Category', value: 'category', isDisplayed: true },
  { label: 'Duty Cost', value: 'dutyCost', isDisplayed: true },
];

const steps = [
  {
    id: 1,
    title: 'Recipient & Format',
    description: 'Choose destination and file type',
  },
  { id: 2, title: 'Data Scope', description: 'Select shipment lines' },
  { id: 3, title: 'Column Mapping', description: 'Arrange export columns' },
  { id: 4, title: 'Select Documents', description: 'Select documents to send' },
  { id: 5, title: 'Validate', description: 'Review warnings and errors' },
  { id: 6, title: 'Review Package', description: 'Confirm before sending' },
  { id: 6, title: 'Send & Track', description: 'Transmission complete' },
];

const stepperSurfaceSx = {
  border: '1px solid var(--noaction-color)',
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  borderRadius: 2,
  fill: 'var(--Background-200, var(--bg-colors))',
};

const recipientTypeConfig: Record<
  RecipientType,
  { label: string; icon: typeof Business; description: string }
> = {
  customs_broker: {
    label: 'Customs Broker',
    icon: Business,
    description: 'Licensed customs broker for import clearance',
  },
  erp_system: {
    label: 'ERP System',
    icon: Dns,
    description: 'Enterprise resource planning system',
  },
  third_party_provider: {
    label: '3PL Provider',
    icon: LocalShipping,
    description: 'Third-party logistics provider',
  },
};

const formatConfig: Record<
  TransmissionFormat,
  { label: string; icon: typeof Public; available: boolean }
> = {
  csv: { label: 'CSV Template', icon: Public, available: true },
  xlsx: { label: 'Excel Template', icon: Public, available: true },
  pdf: { label: 'PDF Template', icon: Public, available: true },
  api: { label: 'API (Coming Soon)', icon: Public, available: false },
};

const validateTransmission = (lines: IProduct[]): TransmissionValidation => {
  const errors: TransmissionIssue[] = [];
  const warnings: TransmissionIssue[] = [];

  lines.forEach((line) => {
    if (!line.hts_code) {
      errors.push({
        model_number: line.model_number,
        message: 'Missing HTS/HS code',
      });
    }
    if (!line.country_of_origin) {
      warnings.push({
        model_number: line.model_number,
        message: 'Country of origin is missing',
      });
    }
    if (line.risk_level !== 'Low') {
      warnings.push({
        model_number: line.model_number,
        message: 'Flagged for manual compliance review',
      });
    }
    // Duty columns come from the stored tariff result, so a line without a
    // usable one exports $0.00. Missing-HTS lines already have an error above.
    if (line.hts_code) {
      const insights = line.tariff_result?.insights;
      if (!line.tariff_result) {
        warnings.push({
          model_number: line.model_number,
          message:
            'Not screened: duty will export as $0.00. Run screening first.',
        });
      } else if (insights?.tariffCalculationAvailable === false) {
        warnings.push({
          model_number: line.model_number,
          message: `No tariff calculation${
            insights?.tariffUnavailableReason
              ? ` (${insights.tariffUnavailableReason})`
              : ''
          }: duty will export as $0.00.`,
        });
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors: errors,
    warnings: warnings,
  };
};

export const SendToBrokerWizard: React.FC<ISendToBrokerWizardProps> = ({
  database,
  open,
  handleClose,
  readRecipientEmail = false,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const [exportTemplates, setExportTemplates] = useState<
    Array<IExternalTemplate>
  >([]);

  const [localSearchTermDocs] = useState('');
  const [sortByDocs, setSortByDocs] = useState<ColumnKeys>('created_at');
  const [sortOrderDocs, setSortOrderDocs] = useState<'asc' | 'desc'>('desc');
  const [currentPageDocs, setCurrentPageDocs] = useState(1);
  const [paginationDocs, setPaginationDocs] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 5,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [uploads, setUploads] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSortDocs = (key: ColumnKeys) => {
    const nextSortOrder =
      sortByDocs === key && sortOrderDocs === 'asc' ? 'desc' : 'asc';
    setSortByDocs(key);
    setSortOrderDocs(nextSortOrder);
    fetchUploads();
  };

  const fetchUploads = async () => {
    setIsLoading(true);
    const response = await FileAPI.getUploadsByDatabaseId(
      database?.id ?? '-1',
      {
        page: currentPageDocs,
        limit: 5,
        sortBy: sortByDocs,
        sortOrder: sortOrderDocs,
        search: localSearchTermDocs,
        databaseProductIds: wizardState.selectedLines
          .map((l) => l.database_product_id)
          .join(','),
      },
    );
    if (response?.success && Array.isArray(response.result.data)) {
      setUploads(response.result.data);
      setPaginationDocs(response.result.pagination);
    } else {
      setUploads([]);
      setPaginationDocs({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 5,
        hasNextPage: false,
        hasPreviousPage: false,
      });
    }
    setIsLoading(false);
  };

  const handlePageChangeDocs = (_e: ChangeEvent<unknown>, newPage: number) => {
    setCurrentPageDocs(newPage);
    fetchUploads();
  };

  const [additionalColumns, setAdditionalColumns] = useState<
    Array<IColumnHeader>
  >([]);
  useEffect(() => {
    if (database?.id) {
      BiteDBAPI.getUniqueAdditionalColumnKeysByDatabaseId(database.id).then(
        (response) => {
          if (response?.success && response?.result) {
            const addCols = defaultColumnHeaders.concat(
              response.result
                .filter((h: string) => !isExcludedBrokerColumn(h))
                .map((h: string) => {
                  return { label: h, value: h, isDisplayed: false };
                }),
            );

            setAdditionalColumns(addCols);
            const newWizard = {
              ...wizardState,
              columnHeaders: addCols,
            };
            setWizardState(newWizard);
          }
        },
      );
    }
    // Keyed on the id: callers pass `database` as an inline object, so depending
    // on the object reset the column mapping on every parent re-render.
  }, [database?.id]);

  const fetchExportTemplates = async () => {
    const response = await BiteDBAPI.getExternalTemplatesByGroupId(
      cur_user?.selected_team?.id ?? '-1',
    );
    if (response?.success) {
      setExportTemplates(response.result);
    } else {
      setExportTemplates([]);
    }
  };

  useEffect(() => {
    if (cur_user) {
      fetchExportTemplates();
    }
  }, [cur_user]);

  const [isAddExportTemplateOpen, setIsAddExportTemplateOpen] = useState(false);
  const { data: brokerConnections, refetch: refetchBrokerConnections } =
    useGetBrokersByGroupId(cur_user?.selected_team?.id ?? '-1');

  const [currentPage, setCurrentPage] = useState(1);
  const [, setJumpToPage] = useState(1);
  const [localSearchTerm, setLocalSearchTerm] = useState<string>();

  const handleSearch = (e: any) => {
    setLocalSearchTerm(e.target.value);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setCurrentPage(page);
  };

  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: string) => {
    const newSortOrder = sortBy === key && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(key);
    setSortOrder(newSortOrder);
  };

  const paginationParams: PaginationParams = {
    page: currentPage,
    limit: 5,
    sortBy: sortBy,
    sortOrder: sortOrder,
    exclusions: [],
  };

  const {
    data: productsData,
    pagination: productsPagination,
    isLoading: isProductsLoading,
  } = useGetProductsByDatabaseId(
    database?.id ?? '-1',
    localSearchTerm,
    undefined,
    paginationParams,
  );

  useEffect(() => {
    if (!isProductsLoading) {
      if (
        !productsPagination?.totalPages ||
        productsPagination?.totalPages === 0
      ) {
        setCurrentPage(0);
        setJumpToPage(0);
      } else if (
        currentPage > productsPagination?.totalPages ||
        currentPage < 1
      ) {
        setCurrentPage(1);
        setJumpToPage(1);
      }
    }
  }, [productsData]);

  const [isAddBrokerOpen, setIsAddBrokerOpen] = useState(false);
  const [newBroker, setNewBroker] = useState({
    name: '',
    email: '',
    contact_type: '',
    group_id: cur_user?.selected_team?.id,
  });

  const handleAddBrokerChange = (field: string, value: string) => {
    setNewBroker((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewBrokerSave = async () => {
    newBroker.contact_type = wizardState.recipientType ?? 'customs_broker';
    await ContactAPI.createContact(newBroker);
    setNewBroker({
      name: '',
      email: '',
      contact_type: '',
      group_id: cur_user?.selected_team?.id,
    });
    await refetchBrokerConnections();
    setIsAddBrokerOpen(false);
  };

  const handleNewBrokerCancel = () => {
    setNewBroker({
      name: '',
      email: '',
      contact_type: '',
      group_id: cur_user?.selected_team?.id,
    });
    setIsAddBrokerOpen(false);
  };

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState<boolean>(false);

  const openConfirmationDelete = () => {
    setIsDeleteConfirmationOpen(true);
  };

  const handleBrokerDelete = async (id: string) => {
    await ContactAPI.deleteContact(id);
    await refetchBrokerConnections();
    setIsDeleteConfirmationOpen(false);
  };

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [transmissionId, setTransmissionId] = useState<string | null>(null);
  const [draggingHeaderIndex, setDraggingHeaderIndex] = useState<number | null>(
    null,
  );
  const [wizardState, setWizardState] = useState<SendToBrokerWizardState>({
    recipientType: null,
    recipientId: null,
    format: 'xlsx',
    formatId: 'xlsx',
    selectedLines: [],
    selectedDocuments: [],
    columnHeaders: additionalColumns,
    includeBomBreakdown: false,
    includeEvidence: true,
    acknowledgeWarnings: false,
    notes: '',
  });

  const filteredConnections = useMemo(() => {
    if (!wizardState.recipientType) return [];
    return brokerConnections.filter(
      (connection: IBroker) =>
        connection.contact_type === wizardState.recipientType,
    );
  }, [wizardState.recipientType, isAddBrokerOpen, isDeleteConfirmationOpen]);

  const selectedRecipient = useMemo(() => {
    return brokerConnections.find(
      (connection: IBroker) => connection.id === wizardState.recipientId,
    );
  }, [wizardState.recipientId]);

  const totals = useMemo(() => {
    const totalValue = wizardState.selectedLines
      .reduce(
        (sum, line) => {
          const savedTR = line.tariff_result ?? {};
          const math = savedTR.math ?? {};

          const dutyCost = Number(math.dutyCost ?? 0);

          const productValue = Number(line.product_value ?? 0);

          // Stored math.totalCost includes MPF and HMF; broker totals exclude both.
          const totalCost =
            math.totalCost !== null && math.totalCost !== undefined
              ? Number(math.totalCost) -
                Number(math.mpf ?? 0) -
                Number(math.hmf ?? 0)
              : productValue + dutyCost;

          return sum + line.quantity * totalCost;
        },
        0, // 👈 initial value is required
      )
      .toFixed(2);
    const totalQuantity = wizardState.selectedLines.reduce(
      (sum, line) => sum + line.quantity,
      0,
    );
    return { totalValue: totalValue, totalQuantity: totalQuantity };
  }, [wizardState.selectedLines]);

  const validation = useMemo(() => {
    if (wizardState.selectedLines.length === 0) return null;
    return validateTransmission(wizardState.selectedLines);
  }, [wizardState.selectedLines]);

  const resetWizardState = () => {
    setCurrentStep(1);
    setJumpToPage(1);
    setCurrentPage(1);
    setTransmissionId(null);
    setDraggingHeaderIndex(null);
    setWizardState({
      recipientType: null,
      recipientId: null,
      format: 'xlsx',
      formatId: 'xlsx',
      selectedLines: [],
      selectedDocuments: [],
      columnHeaders: additionalColumns,
      includeBomBreakdown: false,
      includeEvidence: true,
      acknowledgeWarnings: false,
      notes: '',
    });
  };

  const handleModalClose = () => {
    resetWizardState();
    handleClose();
  };

  const [isSending, setIsSending] = useState(false);
  const bomBreakdownAvailable =
    (database?.database_structure ?? 'hierarchy') === 'hierarchy' &&
    wizardState.format !== 'api' &&
    wizardState.selectedLines.some((line) => childProductCount(line) > 0);

  const handleNext = async () => {
    const includeBomBreakdown =
      bomBreakdownAvailable && wizardState.includeBomBreakdown;
    if (currentStep === 6) {
      if (wizardState.format === 'api') {
        const selectedTemplate = exportTemplates.find(
          (t) => t.id === wizardState.formatId,
        );
        if (!selectedTemplate?.field_mapping_id) {
          enqueueSnackbar('This template has no API connection configured.', {
            variant: 'error',
          });
          return;
        }

        setIsSending(true);
        const response = await BiteDBAPI.sendExportViaApi({
          database_id: database?.id ?? '',
          field_mapping_id: selectedTemplate.field_mapping_id,
        });
        setIsSending(false);

        if (response?.success) {
          setTransmissionId(response.result.uploadId);
          enqueueSnackbar('Export generated successfully!', {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(
            typeof response?.result === 'string'
              ? response.result
              : 'Failed to generate export.',
            { variant: 'error' },
          );
          return;
        }
      } else {
        let bomBreakdown: Record<string, BrokerBomRow[]> | undefined =
          undefined;
        if (includeBomBreakdown && database?.id) {
          setIsSending(true);
          try {
            bomBreakdown = await loadBomBreakdown(
              wizardState.selectedLines,
              String(database.id),
            );
          } catch (error) {
            setIsSending(false);
            enqueueSnackbar(
              error instanceof Error
                ? error.message
                : 'Failed to load BOM components',
              { variant: 'error' },
            );
            return;
          }
        }
        try {
          setIsSending(true);
          const newId = `tx-${Date.now()}`;
          setTransmissionId(newId);
          const fullName = [
            cur_user.basics?.firstName,
            cur_user.basics?.lastName,
          ]
            .filter(Boolean)
            .join(' ');
          const recipients = brokerConnections.filter(
            (c) => c.id === wizardState.recipientId,
          );
          const response = await EmailAPI.sendToBroker({
            recipientType: wizardState.recipientType ?? '',
            recipientId: wizardState.recipientId ?? '',
            recipient: recipients.length > 0 ? recipients[0].name : '',
            from: fullName,
            recipientEmail: recipients.length > 0 ? recipients[0].email : '',
            headers: wizardState.columnHeaders,
            format: wizardState.format ?? 'csv',
            selectedLines: wizardState.selectedLines.map(
              withResolvedBrokerProductValue,
            ),
            selectedDocuments: wizardState.selectedDocuments,
            includeBomBreakdown: includeBomBreakdown,
            bomBreakdown: bomBreakdown,
            includeEvidence: wizardState.includeEvidence,
            acknowledgeWarnings: wizardState.acknowledgeWarnings,
            notes: wizardState.notes,
          });
          if (!response?.success) {
            throw new Error('Package send failed');
          }
          enqueueSnackbar('Package sent successfully!', {
            variant: 'success',
          });
        } catch (e) {
          enqueueSnackbar('Package error while sending!', {
            variant: 'error',
          });
          return;
        } finally {
          setIsSending(false);
        }
      }
    }
    setCurrentStep((prev) => Math.min(prev + 1, 7));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const toggleLineSelection = (line: IProduct) => {
    setWizardState((prev) => ({
      ...prev,
      selectedLines: prev.selectedLines.some((l) => l.id === line.id)
        ? prev.selectedLines.filter((l) => l.id !== line.id)
        : [...prev.selectedLines, line],
    }));
  };

  const selectAllLines = () => {
    setWizardState((prev) => {
      const map = new Map(prev.selectedLines.map((line) => [line.id, line]));

      productsData.forEach((line) => {
        map.set(line.id, line);
      });

      return {
        ...prev,
        selectedLines: Array.from(map.values()),
      };
    });
  };

  const deselectAllLines = () => {
    setWizardState((prev) => ({
      ...prev,
      selectedLines: [],
    }));
    fetchUploads();
  };

  const deselectCurrentLines = () => {
    setWizardState((prev) => ({
      ...prev,
      selectedLines: prev.selectedLines.filter(
        (l) => !productsData.some((line) => l.id === line.id),
      ),
    }));
  };

  const handleSelectAllToggle = () => {
    if (
      productsData.filter(
        (p) => !wizardState.selectedLines.some((sl) => sl.id === p.id),
      ).length > 0
    ) {
      selectAllLines();
    } else {
      deselectAllLines();
    }
  };

  const toggleLineSelectionDocuments = (line: IUpload) => {
    setWizardState((prev) => ({
      ...prev,
      selectedDocuments: prev.selectedDocuments.some((l) => l.id === line.id)
        ? prev.selectedDocuments.filter((l) => l.id !== line.id)
        : [...prev.selectedDocuments, line],
    }));
  };

  const selectAllLinesDocuments = () => {
    setWizardState((prev) => {
      const map = new Map(
        prev.selectedDocuments.map((line) => [line.id, line]),
      );

      uploads.forEach((line) => {
        map.set(line.id, line);
      });

      return {
        ...prev,
        selectedDocuments: Array.from(map.values()),
      };
    });
  };

  const deselectAllLinesDocuments = () => {
    setWizardState((prev) => ({
      ...prev,
      selectedDocuments: [],
    }));
  };

  const handleSelectAllToggleDocuments = () => {
    if (
      uploads.filter(
        (p) => !wizardState.selectedDocuments.some((sl) => sl.id === p.id),
      ).length > 0
    ) {
      selectAllLinesDocuments();
    } else {
      deselectAllLinesDocuments();
    }
  };

  useEffect(() => {
    fetchUploads();
  }, [wizardState.selectedLines]);

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 1:
        return (
          !!wizardState.recipientType &&
          !!wizardState.recipientId &&
          !!wizardState.format
        );
      case 2:
        return wizardState.selectedLines.length > 0;
      case 3:
        return wizardState.columnHeaders.length > 0;
      case 4:
        return true;
      case 5:
        if (!validation) return false;
        if (!validation.isValid && !wizardState.acknowledgeWarnings)
          return false;
        if (validation.errors.length > 0 && !wizardState.acknowledgeWarnings)
          return false;
        return true;
      case 6:
        return true;
      default:
        return true;
    }
  };

  const handleColumnDragStart = (index: number) => {
    setDraggingHeaderIndex(index);
  };

  const handleColumnDrop = (targetIndex: number) => {
    if (draggingHeaderIndex === null) {
      return;
    }
    setWizardState((prev) => {
      const dragged = prev.columnHeaders.at(draggingHeaderIndex);
      if (dragged) {
        const nextHeaders = prev.columnHeaders.filter(
          (h) => h.value !== dragged.value,
        );
        nextHeaders.splice(targetIndex, 0, dragged);
        return {
          ...prev,
          columnHeaders: nextHeaders,
        };
      }
      return {
        ...prev,
      };
    });
    setDraggingHeaderIndex(null);
  };

  // Copies instead of mutating: the header objects are shared with the default
  // column list, so mutating them carried ticks into the next wizard session.
  const handleToggleColumnHeader = (index: number) => {
    setWizardState((prev) => ({
      ...prev,
      columnHeaders: prev.columnHeaders.map((header, headerIndex) =>
        headerIndex === index
          ? { ...header, isDisplayed: !header.isDisplayed }
          : header,
      ),
    }));
  };

  const inputFieldSx = {
    height: '36px',
    '& .MuiInputBase-root': {
      backgroundColor: 'var(--input-color)',
      borderRadius: '8px',
      height: '36px',
      paddingRight: '8px',
      color: 'var(--input-text-color)',
      display: 'flex',
      alignSelf: 'stretch',
      border:
        '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
      background: 'var(--input-color)',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '20px',
    },
    '& input': {
      color: 'var(--input-text-color)',
      fontSize: '14px',
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-error:hover fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-focused.Mui-error fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiFormLabel-root': {
      color: 'var(--focused-text-color)',
      fontSize: '14px',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--focused-text-color)',
    },
  };

  const textAreaFieldSx = {
    '& .MuiInputBase-root': {
      backgroundColor: 'var(--input-color)',
      borderRadius: '8px',
      paddingRight: '8px',
      color: 'var(--input-text-color)',
      display: 'flex',
      alignSelf: 'stretch',
      border:
        '1px solid var(--Colors-Border-border-primary, var(--bg-border-db))',
      background: 'var(--input-color)',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
      fontFamily: 'Inter',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '20px',
    },
    '& input': {
      color: 'var(--input-text-color)',
      fontSize: '14px',
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-error:hover fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiOutlinedInput-root.Mui-focused.Mui-error fieldset': {
      borderColor: 'var(--input-placeholder-color)',
    },
    '& .MuiFormLabel-root': {
      color: 'var(--focused-text-color)',
      fontSize: '14px',
    },
    '& .MuiSvgIcon-root': {
      color: 'var(--focused-text-color)',
    },
  };

  const [isFileUploadOpen, setIsFileUploadOpen] = useState(false);
  const handleCloseFileUpload = () => {
    setIsFileUploadOpen(false);
    fetchUploads();
  };
  const [product, setProduct] = useState<IProduct>();
  const handleUploadOpen = (uploadProduct: IProduct) => {
    setProduct(uploadProduct);
    setIsFileUploadOpen(true);
  };

  const [isFileUploadOpenDatabase, setIsFileUploadOpenDatabase] =
    useState(false);
  const handleCloseFileUploadDatabase = () => {
    setIsFileUploadOpenDatabase(false);
    fetchUploads();
  };
  const handleUploadOpenDatabase = () => {
    setIsFileUploadOpenDatabase(true);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleModalClose();
      }}
      disableEscapeKeyDown
      className='dialog_box'
      fullWidth
      maxWidth='lg'
      sx={{ color: 'var(--noaction-color)' }}
      PaperProps={{
        className: 'send-broker-dialog-paper',
      }}
    >
      <DialogTitle className='modal-header-content modal-title send-broker-dialog-title'>
        Share Database
        <div
          className='audit-add-modal-head-cont-icon send-broker-close-icon'
          onClick={handleModalClose}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='18'
            height='18'
            viewBox='0 0 18 18'
            fill='none'
          >
            <path
              d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
              stroke='var(--text-primary)'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            ></path>
          </svg>
        </div>
      </DialogTitle>
      <DialogContent
        dividers
        className='send-broker-dialog-content'
        sx={{ maxHeight: '80vh' }}
      >
        <Stepper
          activeStep={currentStep - 1}
          alternativeLabel
          className='send-broker-stepper'
          sx={{
            ...stepperSurfaceSx,
            px: 2,
            py: 2,
            '& .MuiStepIcon-root': {
              color: 'var(--text-secondary-paragraph)',
            },
            '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed':
              {
                color: 'var(--action-color)',
              },
            '& .MuiStepIcon-root.Mui-disabled': {
              color: 'var(--action-color)',
            },
            '& .MuiStepLabel-label': {
              color: 'var(--text-primary)',
            },
            '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed':
              {
                color: 'var(--text-primary)',
              },
          }}
        >
          {steps.map((step) => (
            <Step key={step.id}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box mt={4}>
          {currentStep === 1 && (
            <Box display='flex' flexDirection='column' gap={4}>
              <Box>
                <Typography variant='h6' gutterBottom>
                  Select Recipient Type:
                </Typography>
                <Box
                  display='grid'
                  gridTemplateColumns='repeat(3, 1fr)'
                  gap={2}
                >
                  {(
                    Object.entries(recipientTypeConfig) as [
                      RecipientType,
                      typeof recipientTypeConfig.customs_broker,
                    ][]
                  ).map(([type, config]) => {
                    const Icon = config.icon;
                    const isSelected = wizardState.recipientType === type;
                    return (
                      <Card
                        key={type}
                        onClick={() =>
                          setWizardState((prev) => ({
                            ...prev,
                            recipientType: type,
                            recipientId: null,
                          }))
                        }
                        sx={{
                          cursor: 'pointer',
                          border: isSelected
                            ? '1px solid'
                            : '1px solid transparent',
                          borderColor: isSelected
                            ? 'var(--action-color)'
                            : 'var(--border-color)',
                          backgroundColor: isSelected
                            ? 'primary.50'
                            : 'var(--Background-200, var(--bg-colors))',
                          transition: 'border-color 0.2s ease',
                          borderRadius: '8px',
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Icon color='primary' sx={{ fontSize: 32, mb: 1 }} />
                          <Typography variant='subtitle1' fontWeight={600}>
                            {config.label}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {config.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </Box>

              {wizardState.recipientType && (
                <Box>
                  <Typography variant='h6' gutterBottom>
                    Select Destination:
                  </Typography>
                  {filteredConnections.length > 0 ? (
                    <Box display='flex' flexDirection='column' gap={2}>
                      {filteredConnections.map((connection: IBroker) => {
                        const isSelected =
                          wizardState.recipientId === connection.id;
                        return (
                          <Card
                            key={connection.id}
                            onClick={() =>
                              setWizardState((prev) => ({
                                ...prev,
                                recipientId: !isSelected ? connection.id : null,
                              }))
                            }
                            sx={{
                              cursor: 'pointer',
                              border: isSelected
                                ? '1px solid'
                                : '1px solid transparent',
                              borderColor: isSelected
                                ? 'var(--action-color)'
                                : 'var(--border-color)',
                              backgroundColor: isSelected
                                ? 'primary.50'
                                : 'background.paper',
                              transition: 'border-color 0.2s ease',
                              borderRadius: '8px',
                            }}
                          >
                            <CardContent
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Box>
                                <Typography fontWeight={600}>
                                  {connection.name}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  {connection.email}
                                </Typography>
                              </Box>
                              <Box display='flex' alignItems='right' gap={1}>
                                {!isDeleteConfirmationOpen && isSelected && (
                                  <CheckCircle
                                    sx={{ fill: 'var(--action-color)' }}
                                  />
                                )}

                                {!isDeleteConfirmationOpen && (
                                  <DeleteIcon
                                    sx={{ fill: 'var(--action-color)' }}
                                    onClick={openConfirmationDelete}
                                  />
                                )}

                                {isDeleteConfirmationOpen && isSelected && (
                                  <>
                                    <Button
                                      variant='outlined'
                                      onClick={() =>
                                        handleBrokerDelete(connection.id)
                                      }
                                    >
                                      Delete
                                    </Button>
                                    {'    '}
                                    <Button
                                      variant='outlined'
                                      onClick={() =>
                                        setIsDeleteConfirmationOpen(false)
                                      }
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                      {isAddBrokerOpen && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            margin: '16px 0',
                          }}
                        >
                          <InputLabel>Name:</InputLabel>
                          <TextField
                            type='text'
                            style={{ width: '300px' }}
                            sx={inputFieldSx}
                            onChange={(e: any) =>
                              handleAddBrokerChange('name', e.target.value)
                            }
                          />
                          {'    '}
                          <InputLabel>Email:</InputLabel>
                          <TextField
                            type='text'
                            style={{ width: '300px' }}
                            sx={inputFieldSx}
                            onChange={(e: any) =>
                              handleAddBrokerChange('email', e.target.value)
                            }
                          />
                          <br />
                          <Button
                            variant='outlined'
                            size='small'
                            onClick={handleNewBrokerSave}
                          >
                            Save
                          </Button>
                          {'    '}
                          <Button
                            variant='outlined'
                            size='small'
                            onClick={handleNewBrokerCancel}
                          >
                            Cancel
                          </Button>
                        </Box>
                      )}
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={() => setIsAddBrokerOpen(true)}
                        sx={{ alignSelf: 'flex-start', px: 1.5, py: 0.5 }}
                      >
                        Add New{' '}
                        {recipientTypeConfig[wizardState.recipientType].label}
                      </Button>
                    </Box>
                  ) : (
                    <Card
                      sx={{
                        backgroundColor:
                          'var(--Background-200, var(--bg-colors))',
                        boxShadow: 'none',
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography color='text.secondary' gutterBottom>
                          No connections configured
                        </Typography>
                        {isAddBrokerOpen && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              margin: '16px 0',
                            }}
                          >
                            <InputLabel>Name:</InputLabel>
                            <TextField
                              type='text'
                              style={{ width: '300px' }}
                              sx={inputFieldSx}
                              onChange={(e: any) =>
                                handleAddBrokerChange('name', e.target.value)
                              }
                            />
                            {'    '}
                            <InputLabel>Email:</InputLabel>
                            <TextField
                              type='text'
                              style={{ width: '300px' }}
                              sx={inputFieldSx}
                              onChange={(e: any) =>
                                handleAddBrokerChange('email', e.target.value)
                              }
                            />
                            <br />
                            <Button
                              variant='outlined'
                              size='small'
                              onClick={handleNewBrokerSave}
                            >
                              Save
                            </Button>
                            {'    '}
                            <Button
                              variant='outlined'
                              size='small'
                              onClick={handleNewBrokerCancel}
                            >
                              Cancel
                            </Button>
                          </Box>
                        )}
                        <Button
                          variant='outlined'
                          onClick={() => setIsAddBrokerOpen(true)}
                        >
                          Add{' '}
                          {recipientTypeConfig[wizardState.recipientType].label}
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              {wizardState.recipientId && (
                <Box>
                  <Typography variant='h6' gutterBottom>
                    Select Format:{' '}
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => setIsAddExportTemplateOpen(true)}
                      sx={{ alignSelf: 'flex-start', px: 1.5, py: 0.5 }}
                    >
                      Add New
                    </Button>
                  </Typography>
                  <Box
                    display='grid'
                    gridTemplateColumns='repeat(2, 1fr)'
                    gap={2}
                  >
                    {(
                      Object.entries(formatConfig) as [
                        TransmissionFormat,
                        typeof formatConfig.csv,
                      ][]
                    ).map(([formatKey, config]) => {
                      const Icon = config.icon;
                      const isSelected = wizardState.formatId === formatKey;
                      return (
                        <Card
                          key={formatKey}
                          onClick={() =>
                            config.available &&
                            setWizardState((prev) => ({
                              ...prev,
                              format: formatKey,
                              formatId: formatKey,
                              columnHeaders: additionalColumns,
                            }))
                          }
                          sx={{
                            cursor: config.available
                              ? 'pointer'
                              : 'not-allowed',
                            border: isSelected
                              ? '1px solid'
                              : '1px solid transparent',
                            borderColor: isSelected
                              ? 'var(--action-color)'
                              : 'var(--border-color)',
                            backgroundColor: isSelected
                              ? 'primary.50'
                              : 'background.paper',
                            transition: 'border-color 0.2s ease',
                            opacity: !config.available ? '.5' : '1',
                            borderRadius: '8px',
                          }}
                        >
                          <CardContent
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Icon color='primary' />
                            <Box>
                              <Typography fontWeight={600}>
                                {config.label}
                              </Typography>
                            </Box>
                            {isSelected && (
                              <CheckCircle
                                sx={{ fill: 'var(--action-color)' }}
                              />
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}

                    {exportTemplates.map((template) => {
                      const isSelected = wizardState.formatId === template.id;
                      return (
                        <Card
                          key={template.id}
                          onClick={() =>
                            setWizardState((prev) => ({
                              ...prev,
                              format: template?.export_type ?? 'csv',
                              formatId: template?.id ?? '-1',
                              columnHeaders: template.headers.filter(
                                (header: IColumnHeader) =>
                                  !isExcludedBrokerColumn(header.value),
                              ),
                            }))
                          }
                          sx={{
                            cursor: 'pointer',
                            border: isSelected
                              ? '1px solid'
                              : '1px solid transparent',
                            borderColor: isSelected
                              ? 'var(--action-color)'
                              : 'var(--border-color)',
                            backgroundColor: isSelected
                              ? 'primary.50'
                              : 'background.paper',
                            transition: 'border-color 0.2s ease',
                            opacity: '1',
                          }}
                        >
                          <CardContent
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Box>
                              <Typography fontWeight={600}>
                                {template.name}{' '}
                                {`(File Type: ${template?.export_type})`}
                              </Typography>
                            </Box>
                            {isSelected && (
                              <CheckCircle
                                sx={{ fill: 'var(--action-color)' }}
                              />
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {currentStep === 2 && (
            <Box display='flex' flexDirection='column' gap={4}>
              <Box>
                <Box display='flex' justifyContent='space-between' mb={2}>
                  <Typography variant='h6'>Select Shipment Lines:</Typography>
                  <Box display='flex' gap={1}>
                    <TextField
                      type='text'
                      sx={inputFieldSx}
                      placeholder='Search Products'
                      onChange={handleSearch}
                    />
                  </Box>
                  <Box display='flex' gap={1}>
                    <div
                      className='btn-new-automate'
                      style={{ verticalAlign: 'middle' }}
                      onClick={selectAllLines}
                    >
                      <div>Select</div>
                    </div>
                    <div
                      className='btn-new-automate'
                      style={{ verticalAlign: 'middle' }}
                      onClick={deselectCurrentLines}
                    >
                      <div>Clear</div>
                    </div>
                    <div
                      className='btn-new-automate'
                      style={{ verticalAlign: 'middle' }}
                      onClick={deselectAllLines}
                    >
                      <div>Clear All</div>
                    </div>
                  </Box>
                </Box>
                <Box>
                  <MasterTable
                    cols={
                      <ProductPageColumns
                        showFilters
                        handleCheckboxClick={handleSelectAllToggle}
                        isSelectedItems={
                          productsData?.length > 0 &&
                          productsData.every((line) =>
                            wizardState.selectedLines.some(
                              (l) => l.id === line.id,
                            ),
                          )
                        }
                        sortData={handleSort}
                        sortConfig={{ key: sortBy, direction: sortOrder }}
                      />
                    }
                    loading={isProductsLoading}
                    suppliersData={productsData}
                    location={'/product'}
                    selectedItems={wizardState.selectedLines}
                    setSelectedItems={toggleLineSelection}
                    currentPage={productsPagination?.currentPage || 1}
                    totalPages={productsPagination?.totalPages || 1}
                    onPageChange={handlePageChange}
                    customRowComponent={(props: any) => (
                      <ProductPageRow
                        key={props.transaction.id}
                        transaction={props.transaction}
                        selectedItems={props.selectedItems}
                        setSelectedItem={props.setSelectedItems}
                        aiInProgress={props.aiInProgress}
                        uploadModal={handleUploadOpen}
                      />
                    )}
                  />
                </Box>
                {wizardState.selectedLines.length > 0 && (
                  <Box
                    mt={2}
                    p={2}
                    display='flex'
                    justifyContent='space-between'
                    bgcolor='action.hover'
                    borderRadius={2}
                  >
                    <Typography variant='body2'>
                      <strong>{wizardState.selectedLines.length}</strong> lines
                      selected
                    </Typography>
                    <Typography variant='body2'>
                      Total value:{' '}
                      <strong>${totals.totalValue.toLocaleString()}</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {currentStep === 3 && wizardState.format === 'api' && (
            <Typography color='text.secondary' variant='body2'>
              Column mapping does not apply to CargoWise XML. The field mapping
              on the template controls the output.
            </Typography>
          )}

          {currentStep === 3 && wizardState.format !== 'api' && (
            <Box display='flex' flexDirection='column' gap={3}>
              <Typography color='text.secondary' variant='body2'>
                Drag and drop columns to set the export order. Click a column to
                include or exclude it; checked columns are exported.
              </Typography>
              <Box
                display='flex'
                flexDirection='column'
                gap={2}
                onDragOver={(event: any) => event.preventDefault()}
              >
                {wizardState.columnHeaders.map((header, index) => (
                  <Box
                    key={`${header.value}-${index}`}
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    p={1.5}
                    borderRadius={1}
                    border='1px solid'
                    borderColor='divider'
                    bgcolor='background.paper'
                    draggable
                    onDragStart={() => handleColumnDragStart(index)}
                    onDragOver={(event: any) => event.preventDefault()}
                    onDrop={() => handleColumnDrop(index)}
                    onClick={() => handleToggleColumnHeader(index)}
                    sx={{
                      cursor: 'grab',
                      border: header.isDisplayed
                        ? '1px solid'
                        : '1px solid transparent',
                      borderColor: header.isDisplayed
                        ? 'primary.main'
                        : 'divider',
                      backgroundColor: 'primary.50',
                      opacity: draggingHeaderIndex === index ? 0.6 : undefined,
                    }}
                  >
                    <Box>
                      <Typography fontWeight={600}>{header.label}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {header.value}
                      </Typography>
                    </Box>
                    <Box>
                      {header.isDisplayed && (
                        <CheckCircle sx={{ fill: 'var(--action-color)' }} />
                      )}
                    </Box>
                  </Box>
                ))}

                {wizardState.columnHeaders.length === 0 && (
                  <Box
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    py={4}
                    borderRadius={1}
                    border='1px dashed'
                    borderColor='divider'
                    color='text.secondary'
                  >
                    <Typography variant='body2'>
                      No columns selected. Add at least one header to continue.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {currentStep === 4 && (
            <Box display='flex' flexDirection='column' gap={3}>
              <Box display='flex' gap={1}>
                <div
                  className='btn-new-automate'
                  style={{ verticalAlign: 'middle' }}
                  onClick={handleUploadOpenDatabase}
                >
                  <FileUploadIcon /> Upload To Database
                </div>
              </Box>
              <Box>
                <MasterTable
                  cols={
                    <UploadColumns
                      sortData={handleSortDocs}
                      sortConfig={{ key: sortByDocs, direction: sortOrderDocs }}
                      handleCheckboxClick={handleSelectAllToggleDocuments}
                      isSelectedItems={
                        uploads?.length > 0 &&
                        uploads.every((line) =>
                          wizardState.selectedDocuments.some(
                            (l) => l.id === line.id,
                          ),
                        )
                      }
                    />
                  }
                  suppliersData={uploads}
                  selectedItems={wizardState.selectedDocuments}
                  setSelectedItems={toggleLineSelectionDocuments}
                  loading={isLoading}
                  currentPage={paginationDocs.currentPage}
                  totalPages={paginationDocs.totalPages}
                  onPageChange={handlePageChangeDocs}
                  customRowComponent={(props: any) => (
                    <UploadRow
                      keyVal={props.transaction.id}
                      transaction={props.transaction}
                      selectedItems={props.selectedItems}
                      setSelectedItems={props.setSelectedItems}
                    />
                  )}
                />
              </Box>
              {wizardState.selectedDocuments.length > 0 && (
                <Box
                  mt={2}
                  p={2}
                  display='flex'
                  justifyContent='space-between'
                  bgcolor='action.hover'
                  borderRadius={2}
                >
                  <Typography variant='body2'>
                    <strong>{wizardState.selectedDocuments.length}</strong>{' '}
                    lines selected
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {currentStep === 5 && (
            <Box display='flex' flexDirection='column' gap={3}>
              <Typography variant='h6'>Validation Results</Typography>
              {validation && (
                <>
                  {validation.errors.length > 0 && (
                    <Box severity='error' icon={<ErrorOutline />}>
                      <AlertTitle>
                        Errors Found ({validation.errors.length})
                      </AlertTitle>
                      <Box component='ul' sx={{ pl: 2, m: 0 }}>
                        {validation.errors.map((error, index) => (
                          <li
                            key={`${error.model_number ?? 'general'}-${index}`}
                          >
                            <Typography variant='body2'>
                              <strong>
                                {error.model_number || 'General'}:
                              </strong>{' '}
                              {error.message}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {validation.warnings.length > 0 && (
                    <Box severity='warning' icon={<WarningAmber />}>
                      <AlertTitle>
                        Warnings ({validation.warnings.length})
                      </AlertTitle>
                      <Box component='ul' sx={{ pl: 2, m: 0 }}>
                        {validation.warnings.map((warning, index) => (
                          <li
                            key={`${
                              warning.model_number ?? 'general'
                            }-${index}`}
                          >
                            <Typography variant='body2'>
                              <strong>
                                {warning.model_number || 'General'}:
                              </strong>{' '}
                              {warning.message}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {validation.isValid && validation.warnings.length === 0 && (
                    <Box
                      severity='success'
                      icon={<CheckCircle sx={{ color: 'white' }} />}
                    >
                      <AlertTitle>All Checks Passed</AlertTitle>
                      <Typography variant='body2'>
                        All selected items have complete data and no blocking
                        issues.
                      </Typography>
                    </Box>
                  )}

                  {(validation.errors.length > 0 ||
                    validation.warnings.length > 0) && (
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={wizardState.acknowledgeWarnings}
                            onChange={(event: any) =>
                              setWizardState((prev) => ({
                                ...prev,
                                acknowledgeWarnings: event.target.checked,
                              }))
                            }
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight={600}>
                              Send anyway with acknowledgment
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                              I acknowledge the issues above and accept
                              responsibility for sending incomplete data
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {currentStep === 6 && (
            <Box display='flex' flexDirection='column' gap={3}>
              <Typography variant='h6'>Package Summary</Typography>
              <Box display='grid' gridTemplateColumns='repeat(2, 1fr)' gap={2}>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Recipient
                  </Typography>
                  <Typography fontWeight={600}>
                    {selectedRecipient?.name}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {readRecipientEmail
                      ? selectedRecipient?.email
                      : selectedRecipient?.contactEmail}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Format
                  </Typography>
                  <Typography fontWeight={600}>
                    {formatConfig[wizardState.format].label}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Line Items
                  </Typography>
                  <Typography fontWeight={600}>
                    {wizardState.selectedLines.length} items
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Total Value
                  </Typography>
                  <Typography fontWeight={600}>
                    ${totals.totalValue.toLocaleString()}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    USD
                  </Typography>
                </Box>
              </Box>

              {bomBreakdownAvailable && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={wizardState.includeBomBreakdown}
                      onChange={(event: any) =>
                        setWizardState((prev) => ({
                          ...prev,
                          includeBomBreakdown: event.target.checked,
                        }))
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={600}>
                        Include BOM breakdown
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        List each assembly&apos;s components as indented rows
                        under it in the attachment, using the BOM quantity, unit
                        of measure, and unit value.
                      </Typography>
                    </Box>
                  }
                />
              )}

              <label>Notes (optional message to send in email): </label>
              <TextField
                multiline={true}
                minRows={3}
                value={wizardState.notes}
                sx={textAreaFieldSx}
                onChange={(event: any) =>
                  setWizardState((prev) => ({
                    ...prev,
                    notes: event.target.value,
                  }))
                }
              />
            </Box>
          )}

          {currentStep === 7 && (
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              gap={3}
              py={3}
            >
              <Box
                width={34}
                height={34}
                bgcolor='success.light'
                borderRadius='50%'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <CheckCircle sx={{ fontSize: 32 }} />
              </Box>
              <Box textAlign='center'>
                <Typography variant='h5' fontWeight={600}>
                  Package Sent Successfully
                </Typography>
                <Typography color='text.secondary'>
                  Your compliance package has been sent to{' '}
                  {selectedRecipient?.name}
                </Typography>
              </Box>
              <Box sx={{ width: '300px' }}>
                <Box display='flex' justifyContent='space-between' mb={1}>
                  <Typography variant='body2' color='text.secondary'>
                    Package ID
                  </Typography>
                  <Typography variant='body2' component='code'>
                    {transmissionId}
                  </Typography>
                </Box>
                <Box display='flex' justifyContent='space-between' mb={1}>
                  <Typography variant='body2' color='text.secondary'>
                    Sent At
                  </Typography>
                  <Typography variant='body2'>
                    {format(new Date(), 'MMM d, yyyy h:mm a')}
                  </Typography>
                </Box>
                <Box display='flex' justifyContent='space-between' mb={1}>
                  <Typography variant='body2' color='text.secondary'>
                    Format
                  </Typography>
                  <Typography variant='body2'>
                    {formatConfig[wizardState.format].label}
                  </Typography>
                </Box>
                <Box display='flex' justifyContent='space-between'>
                  <Typography variant='body2' color='text.secondary'>
                    Line Items
                  </Typography>
                  <Typography variant='body2'>
                    {wizardState.selectedLines.length}
                  </Typography>
                </Box>
              </Box>
              <Box display='flex' gap={2}>
                <div
                  className='btn-new-automate'
                  style={{ verticalAlign: 'middle' }}
                  onClick={handleModalClose}
                >
                  Done
                </div>
              </Box>
            </Box>
          )}
        </Box>

        {currentStep < 7 && (
          <Box display='flex' justifyContent='space-between' mt={4}>
            <div
              className={
                currentStep === 1
                  ? 'btn-new-automate disabled'
                  : 'btn-new-automate'
              }
              style={{ verticalAlign: 'middle' }}
              onClick={() => handleBack()}
            >
              Back
            </div>
            <div
              className={
                canProceed() && !isSending
                  ? 'btn-new-automate'
                  : 'btn-new-automate disabled'
              }
              style={{ verticalAlign: 'middle' }}
              onClick={() => (canProceed() && !isSending ? handleNext() : null)}
            >
              {currentStep === 6
                ? isSending
                  ? 'Sending…'
                  : 'Send Package'
                : 'Next'}
            </div>
          </Box>
        )}
      </DialogContent>
      <AddExportTemplateModal
        open={isAddExportTemplateOpen}
        handleClose={() => setIsAddExportTemplateOpen(false)}
        databaseId={database?.id ?? '-1'}
        handleRefresh={fetchExportTemplates}
      />
      <AddUploadModal
        database={{ id: product?.database_id, name: product?.database_name }}
        product={product}
        open={isFileUploadOpen}
        handleClose={handleCloseFileUpload}
      />
      <AddUploadModal
        database={database}
        open={isFileUploadOpenDatabase}
        handleClose={handleCloseFileUploadDatabase}
      />
    </Dialog>
  );
};
